export interface DeliveryCoords {
  latitude: number
  longitude: number
}

interface AddressWithCoords {
  latitude?: number | string | null
  longitude?: number | string | null
}

/** Филиал (или любая точка) с координатами — для отсева точки филиала. */
export interface BranchPointLike {
  latitude?: number | string | null
  longitude?: number | string | null
}

/**
 * Допуск сравнения координат: 1e-6° ≈ 0,1 м. Тот же, что в
 * entities/institution/lib/branchPoint.ts и в DeliveryPointGuardService на сервере;
 * здесь продублирован, чтобы чистая lib-функция не тянула сущность целиком.
 */
const SAME_POINT_EPSILON = 1e-6

/**
 * Координаты адреса доставки для заказа и preview-расчёта: по ним сервер подбирает зону
 * и филиал и котирует Яндекс Доставку.
 *
 * Главный источник — выбранный адрес: строка и координаты записываются в него вместе.
 * Координаты из стора оформления (`deliveryCoords`) к строке не привязаны и отстают, когда
 * адрес меняется мимо «Сохранить»: клиент сдвинул карту за зону и закрыл окно, подставился
 * избранный адрес. Раньше они были приоритетнее — и заказ уходил с новым адресом по старой
 * точке, в чужую зону. Остались запасным вариантом для адресов старых клиентов, сохранённых
 * без координат.
 *
 * Точка, совпадающая с координатами филиала, — не адрес клиента (утечка стартовой точки
 * карты, заказ #8300): такие координаты отбрасываются из обоих источников, и сервер получит
 * запрос без координат — он ответит «укажите адрес на карте», а фронт откроет карту.
 */
export function pickDeliveryCoords(
  selectedAddress: AddressWithCoords | null | undefined,
  confirmedOnMap: DeliveryCoords | null | undefined,
  branches?: readonly BranchPointLike[] | null,
): DeliveryCoords | null {
  return toCoords(selectedAddress, branches) ?? toCoords(confirmedOnMap, branches)
}

function toCoords(
  source: AddressWithCoords | null | undefined,
  branches: readonly BranchPointLike[] | null | undefined,
): DeliveryCoords | null {
  // `Number(null)` даёт 0, а не NaN: адрес без координат уехал бы на сервер точкой 0/0
  // (Гвинейский залив) вместо фолбэка по строке адреса.
  if (source?.latitude == null || source?.longitude == null) return null
  const latitude = Number(source.latitude)
  const longitude = Number(source.longitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  if (isAtBranchPoint(latitude, longitude, branches)) return null
  return { latitude, longitude }
}

function isAtBranchPoint(
  latitude: number,
  longitude: number,
  branches: readonly BranchPointLike[] | null | undefined,
): boolean {
  if (!branches?.length) return false
  return branches.some((b) => {
    const bLat = Number(b.latitude)
    const bLng = Number(b.longitude)
    if (!Number.isFinite(bLat) || !Number.isFinite(bLng)) return false
    return Math.abs(latitude - bLat) < SAME_POINT_EPSILON && Math.abs(longitude - bLng) < SAME_POINT_EPSILON
  })
}
